// apps/web/src/pages/Public/MagicLink/components/signatureProvider.ts
//
// Ponto ÚNICO de decisão de qual provider de assinatura renderizar.
// Nenhum outro arquivo deve inspecionar URL/host para inferir provider.

/** Providers de assinatura suportados pelo embed do frontend. */
export type SignatureProviderId = 'clicksign' | 'zapsign';

/**
 * Dados do registro de assinatura devolvidos pelo backend
 * (`POST /p/:token/signature/initiate` e `GET /p/:token/signature/status`, ou o
 * initiate do contrato de first-access).
 *
 * `provider` é autoritativo quando presente. Registros legados que não o
 * expõem usam o host de `envelopeUrl` como fallback.
 */
export interface SignatureEmbedSource {
  /** Provider gravado no registro de assinatura, quando o backend o expõe. */
  provider?: string | null;
  /** URL pronta do envelope/documento (ZapSign: página de assinatura do signatário). */
  envelopeUrl?: string | null;
}

const ZAPSIGN_HOST_SUFFIX = 'zapsign.com.br';

/** Signing grants are only embedded or opened on the provider's HTTPS signing pages. */
export function isZapsignSigningUrl(value: string | null | undefined): value is string {
  if (!value) return false;
  try {
    const url = new URL(value);
    return (
      url.protocol === 'https:' &&
      !url.username &&
      !url.password &&
      !url.port &&
      (url.hostname === ZAPSIGN_HOST_SUFFIX || url.hostname.endsWith(`.${ZAPSIGN_HOST_SUFFIX}`)) &&
      /^\/verificar\/[^/]+\/?$/.test(url.pathname)
    );
  } catch {
    return false;
  }
}

function hostOf(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}

/**
 * Resolve o provider de um registro de assinatura.
 *
 * Ordem: `provider` explícito > host do `envelopeUrl` > `clicksign` (default
 * histórico — documentos criados antes da virada continuam na ClickSign).
 */
export function resolveSignatureProvider(source: SignatureEmbedSource): SignatureProviderId {
  const explicit = source.provider?.trim().toLowerCase();
  if (explicit === 'zapsign' || explicit === 'clicksign') {
    return explicit;
  }

  const host = source.envelopeUrl ? hostOf(source.envelopeUrl) : null;
  if (host && (host === ZAPSIGN_HOST_SUFFIX || host.endsWith(`.${ZAPSIGN_HOST_SUFFIX}`))) {
    return 'zapsign';
  }

  return 'clicksign';
}
